import type { MarketCapResult } from "@mosaic/shared";

/**
 * A market capitalisation, and the ways there can be none.
 *
 * The figure is a price multiplied by a share count from EDGAR, two facts with two dates,
 * so it renders both: one "as of" would state a currency the inputs do not have, and on a
 * separation the share count is what a distribution changes.
 *
 * **Every absence says which absence it is.** No ticker means not yet trading; a missing
 * price is a provider down; a missing share count is EDGAR facts not fetched yet; an
 * unconfigured deployment has no price source. A blank cell says none of these, and reads
 * as zero.
 */

const UNAVAILABLE: Record<string, string> = {
  not_configured: "no price source configured",
  no_ticker: "not yet trading",
  no_price: "price unavailable",
  no_shares: "share count not fetched",
};

/**
 * A market cap at a readable magnitude, truncated to one decimal with a T, B or M
 * suffix ("$380.0B"); below a million, the whole figure grouped. The caller shows the exact
 * figure beside it.
 *
 * @param amount A non-negative decimal string.
 * @param currency An ISO currency code; USD renders as `$`.
 */
export function formatMarketCap(amount: string, currency: string): string {
  const [whole = "0"] = amount.split(".");
  const digits = whole.length;
  const symbol = currency === "USD" ? "$" : `${currency} `;

  const scaled = (divisorDigits: number, suffix: string): string => {
    const cut = whole.length - divisorDigits;
    const head = whole.slice(0, cut) || "0";
    const tail = whole.slice(cut, cut + 1);
    return `${symbol}${head}.${tail}${suffix}`;
  };

  if (digits > 12) return scaled(12, "T");
  if (digits > 9) return scaled(9, "B");
  if (digits > 6) return scaled(6, "M");
  return `${symbol}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

/**
 * A market cap with its price, share count and their dates, or a phrase naming why there is
 * none. Renders nothing while the result is undefined.
 */
export function MarketCapValue({ result }: { result: MarketCapResult | undefined }) {
  if (!result) return null;

  if (!result.available) {
    return <span className="mc-none">{UNAVAILABLE[result.reason] ?? result.reason}</span>;
  }

  const { value, price, priceAsOf, shares, sharesAsOf, source, sharesStale } = result.marketCap;
  return (
    <span className="mc">
      <span
        className="mc-value"
        /* The exact figure, so the truncated one can be checked rather than trusted. */
        title={`${value.amount} ${value.currency}`}
      >
        {formatMarketCap(value.amount, value.currency)}
      </span>
      <span className="mc-basis">
        {price.amount} × {Number(shares).toLocaleString("en-US")} shares
      </span>
      <span className="mc-dates">
        price {priceAsOf} · {source} · shares {sharesAsOf}
        {/*
          Flagged, not hidden: on a separation the share count is the half most likely to
          be out of date.
        */}
        {sharesStale && <span className="mc-stale"> · share count over a quarter old</span>}
      </span>
    </span>
  );
}
